import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { ZitadelProvider } from './auth/ZitadelProvider';
import { RolePreviewProvider } from './auth/RolePreviewProvider';
import { installPreviewFetchGuard } from './auth/role-preview';
import { BackendStatusProvider } from './state/backend-status';
import { loadRuntimeConfig, type RuntimeConfig } from './config/runtime-config';
import { applySecurityHeaders } from './config/security-headers';
import { startRuntimeConfigPolling } from './config/runtime-config-reload';
import { bootProbe, reportError } from './observability/telemetry';
import { registerServiceWorker } from './observability/sw-register';
import { setMockAuthDemoMode } from './auth/mock-auth-loader';
import { I18nProvider } from './i18n';
import { HelpProvider } from './help';
import { ConfigErrorScreen } from './shell/ConfigErrorScreen';
import { SW_UPDATE_READY_EVENT, startNewBuildWatch } from './shell/new-build';
import './styles/global.css';
import './styles/print.css';
import './design-system/tokens.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

async function bootstrap() {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element #root not found in document.');
  }

  let config: RuntimeConfig | null = null;
  let configError: string | null = null;
  try {
    config = await loadRuntimeConfig();
    bootProbe('config_loaded');
  } catch (err) {
    configError = err instanceof Error ? err.message : 'Unknown runtime-config error';
    reportError(err, { feature: 'bootstrap', op: 'load_runtime_config' });
  }

  // Apply security headers AFTER the runtime-config has loaded
  // so the CSP `connect-src` includes the real api/ws hosts.
  // The `import.meta.env.PROD` check is replaced at build time by
  // Vite/Rollup so the dev branch is tree-shaken from production.
  // nginx sets the HTTP-header-only directives (frame-ancestors,
  // X-Frame-Options, HSTS, Permissions-Policy) independently.
  if (import.meta.env.PROD) {
    applySecurityHeaders();
  }
  bootProbe('security_headers_applied');

  if (!config) {
    createRoot(rootEl).render(
      <StrictMode>
        <ConfigErrorScreen message={configError ?? 'Unknown error'} />
      </StrictMode>,
    );
    bootProbe('config_error_screen_rendered');
    return;
  }

  // Persist the loaded config globally for downstream providers before render.
  (window as unknown as { __RUNTIME_CONFIG__?: RuntimeConfig }).__RUNTIME_CONFIG__ = config;

  // "View as role" write block for the call sites that use `fetch`
  // directly (apiFetch checks for itself). Installed before anything
  // renders, so a reload during a preview can never send a write.
  installPreviewFetchGuard();

  // Detect local-demo deployments (authMode=mock + localhost
  // apiBaseUrl) so the docker demo image can ship with mock-auth
  // available. Real production tenants always use authMode=zitadel
  // and never trigger this branch.
  const isLocalDemo =
    config.authMode === 'mock' &&
    (() => {
      try {
        const api = new URL(config.apiBaseUrl);
        return api.hostname === 'localhost' || api.hostname === '127.0.0.1';
      } catch {
        return false;
      }
    })();
  setMockAuthDemoMode(isLocalDemo);
  bootProbe('mock_auth_demo_mode', { enabled: isLocalDemo ? 1 : 0 });

  // Capture and report global runtime errors that aren't caught
  // by an ErrorBoundary (window.onerror, unhandled promise
  // rejections).
  if (typeof window !== 'undefined') {
    window.addEventListener('error', (ev) => {
      reportError(ev.error ?? new Error(ev.message), { feature: 'window' });
    });
    window.addEventListener('unhandledrejection', (ev) => {
      reportError(ev.reason, { feature: 'unhandledrejection' });
    });
  }

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <BrowserRouter>
            <ZitadelProvider>
              {/* Directly inside auth and above everything that reads a
                  role: while "View as role" is active it re-provides the
                  auth context with the previewed role. */}
              <RolePreviewProvider>
                <HelpProvider>
                  <BackendStatusProvider>
                    <App />
                  </BackendStatusProvider>
                </HelpProvider>
              </RolePreviewProvider>
            </ZitadelProvider>
          </BrowserRouter>
        </I18nProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
  // Defer the final "boot complete" probe one tick so React's
  // first paint has actually happened.
  setTimeout(() => bootProbe('react_first_paint'), 0);

  // Register the service-worker. In dev we skip (HMR breaks otherwise);
  // the SW lives at /sw.js and pre-caches the SPA shell + assets so a
  // hard reload while offline still loads the SPA.
  void registerServiceWorker().catch((err) => {
    reportError(err, { feature: 'sw', op: 'register' });
  });

  // A new service worker taking over this tab may mean a new shell is
  // out. It used to reload the page there and then -- under an operator
  // half way through an edit. It now asks the new-build check, which
  // raises a banner that waits for its Reload button
  // (`shell/new-build.ts`). The first worker of a first visit takes over
  // too (`clients.claim()`), and that is no news: only a change from a
  // worker that was already in charge counts.
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    const hadController = navigator.serviceWorker.controller !== null;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hadController) window.dispatchEvent(new CustomEvent(SW_UPDATE_READY_EVENT));
    });
  }

  // Ask every five minutes, and when the tab is shown again, whether a
  // deploy has gone out since this page loaded (production only).
  startNewBuildWatch();

  // Start the periodic runtime-config poll so feature flags can
  // flip without a redeploy. Polling is a no-op in dev (Vite
  // HMR would surprise the operator).
  startRuntimeConfigPolling();
}

void bootstrap();